import React, { useState, useEffect } from 'react';
import { Shield, Eye, EyeOff, Trophy, AlertCircle } from 'lucide-react';
import { FONTS } from '../../lib/theme';

/**
 * Privacy controls panel for community settings.
 *
 * Props:
 *   settings — { profile_visibility, trip_default_visibility, show_in_leaderboards }
 *   onSave   — callback with updated settings object
 *   onClose  — close panel
 */
const PrivacySettings = ({ settings, onSave, onClose, inline = false }) => {
  // Local copy of settings for editing
  const [profileVisibility, setProfileVisibility] = useState(settings?.profile_visibility || 'public');
  const [tripVisibility, setTripVisibility] = useState(settings?.trip_default_visibility || 'public');
  const [showInLeaderboards, setShowInLeaderboards] = useState(settings?.show_in_leaderboards ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Track whether user has made changes
  const isDirty =
    profileVisibility !== (settings?.profile_visibility || 'public') ||
    tripVisibility !== (settings?.trip_default_visibility || 'public') ||
    showInLeaderboards !== (settings?.show_in_leaderboards ?? true);

  // Reset local state when settings prop changes
  useEffect(() => {
    if (settings) {
      setProfileVisibility(settings.profile_visibility || 'public');
      setTripVisibility(settings.trip_default_visibility || 'public');
      setShowInLeaderboards(settings.show_in_leaderboards ?? true);
    }
  }, [settings]);

  const handleSave = async () => {
    if (!isDirty || saving) return;

    setSaving(true);
    setError(null);

    try {
      await onSave({
        profile_visibility: profileVisibility,
        trip_default_visibility: tripVisibility,
        show_in_leaderboards: showInLeaderboards,
      });
    } catch (err) {
      console.error('[PrivacySettings] Save failed:', err);
      setError(err.message || 'Failed to save privacy settings');
    } finally {
      setSaving(false);
    }
  };

  /** Radio-style option button */
  const VisibilityOption = ({ selected, onClick, icon: Icon, label, description }) => (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
        selected
          ? 'border-teal-500 bg-teal-50'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div
        className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
          selected ? 'border-teal-500' : 'border-gray-300'
        }`}
      >
        {selected && <div className="w-2.5 h-2.5 rounded-full bg-teal-500" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${selected ? 'text-teal-600' : 'text-gray-400'}`} />
          <span
            className={`text-sm ${selected ? 'text-teal-700' : 'text-gray-700'}`}
            style={FONTS.bodyBold}
          >
            {label}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-1" style={FONTS.body}>
          {description}
        </p>
      </div>
    </button>
  );

  const content = (
      <div className="space-y-6">
        {/* Profile Visibility */}
        <div>
          <label className="block text-sm text-gray-700 mb-2" style={FONTS.bodyBold}>
            Who can see your profile?
          </label>
          <div className="space-y-2">
            <VisibilityOption
              selected={profileVisibility === 'public'}
              onClick={() => setProfileVisibility('public')}
              icon={Eye}
              label="Public"
              description="Everyone can see your profile and activity"
            />
            <VisibilityOption
              selected={profileVisibility === 'private'}
              onClick={() => setProfileVisibility('private')}
              icon={EyeOff}
              label="Private"
              description="Only your followers can see your profile"
            />
          </div>
        </div>

        {/* Trip Default Visibility */}
        <div>
          <label className="block text-sm text-gray-700 mb-2" style={FONTS.bodyBold}>
            Default visibility for new trips
          </label>
          <div className="space-y-2">
            <VisibilityOption
              selected={tripVisibility === 'public'}
              onClick={() => setTripVisibility('public')}
              icon={Eye}
              label="Public"
              description="New trips are visible to the community by default"
            />
            <VisibilityOption
              selected={tripVisibility === 'private'}
              onClick={() => setTripVisibility('private')}
              icon={EyeOff}
              label="Private"
              description="New trips are private by default — you can share individually"
            />
          </div>
        </div>

        {/* Leaderboard Toggle */}
        <div>
          <label className="block text-sm text-gray-700 mb-2" style={FONTS.bodyBold}>
            Show me in community leaderboards
          </label>
          <button
            onClick={() => setShowInLeaderboards(!showInLeaderboards)}
            className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 transition-all"
          >
            <div className="flex items-center gap-3">
              <Trophy className={`w-5 h-5 ${showInLeaderboards ? 'text-teal-600' : 'text-gray-400'}`} />
              <div className="text-left">
                <span className="text-sm text-gray-700 block" style={FONTS.bodyBold}>
                  Leaderboards
                </span>
                <span className="text-xs text-gray-500" style={FONTS.body}>
                  {showInLeaderboards
                    ? 'Your stats appear in community rankings'
                    : 'Your stats are hidden from rankings'}
                </span>
              </div>
            </div>
            {/* Toggle switch */}
            <div
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                showInLeaderboards ? 'bg-teal-500' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                  showInLeaderboards ? 'translate-x-5.5 left-[1.375rem]' : 'left-0.5'
                }`}
              />
            </div>
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-600" style={FONTS.body}>{error}</p>
          </div>
        )}

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="w-full py-3 rounded-xl text-sm transition-all flex items-center justify-center gap-2 bg-[#313143] text-white hover:bg-[#3d3d52] disabled:opacity-50 disabled:cursor-not-allowed"
          style={FONTS.bodyBold}
        >
          {saving ? 'Saving...' : 'Save Privacy Settings'}
        </button>
      </div>
  );

  if (inline) return content;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
          <Shield className="w-5 h-5 text-teal-600" />
        </div>
        <h2 className="text-xl text-gray-900" style={FONTS.headingBold}>
          Privacy Settings
        </h2>
      </div>
      {content}
    </div>
  );
};

export default PrivacySettings;
